import { LastActiveSource, UserRole } from './user';
import { FeatureFlag, QuotaUnit } from './tenant';

// Keep in sync with dentallink .../user/api/dto/AdminUsageUserDTO.java
// Same fields as AdminUserDTO plus tenant identity — deliberately a separate backend DTO, since
// the per-tenant users endpoint already knows its tenant from the request.
export interface UsageUserDTO {
  id: string;
  name: string;
  email?: string | null;
  role?: UserRole | null;
  enabled: boolean;
  createdAt?: string | null;
  lastActiveAt?: string | null;
  lastActiveSource?: LastActiveSource | null;
  tenantId?: number | null;
  tenantName?: string | null;
}

// Keep in sync with dentallink .../user/api/dto/AdminUsageDTO.UsageTenantDTO
export interface UsageTenantDTO {
  id: number;
  name?: string | null;
  // False when the tenant exists in our database but has no Keycloak group yet — it has users
  // provisioned nowhere, which is exactly what an adoption question is about.
  groupExists: boolean;
}

// GET /v1/admin/usage/users. Note this is a FOURTH wrapper shape: { data, size, ... } with no
// `total`, since the response is not paginated.
export interface UsageResponse {
  data: UsageUserDTO[];
  size: number;
  generatedAt: string;
  // False when Keycloak could not tell us about activity at all. When false the page must hide
  // its activity figures rather than render everyone as "never active", which would be a
  // confidently wrong answer to the only question the page exists to answer.
  lastActiveAvailable: boolean;
  tenants: UsageTenantDTO[];
}

/** Exclusive activity buckets. They partition the users, so the segments sum to the total. */
export type ActivityBucket = 'today' | 'week' | 'month' | 'older' | 'never';

export const ACTIVITY_BUCKET_LABELS: Record<ActivityBucket, string> = {
  today: 'Today',
  week: '2–7 days ago',
  month: '8–30 days ago',
  older: 'Over 30 days ago',
  never: 'Never'
};

/**
 * A user row with its display strings precomputed. Template-called functions re-run on every
 * change-detection pass; this table is long enough that it is worth doing the work once, when
 * the response lands and we are already iterating to aggregate.
 */
export interface UsageUserVM extends UsageUserDTO {
  tenantLabel: string;
  roleLabel: string;
  roleClass: string;
  statusLabel: string;
  statusClass: string;
  lastActiveText: string;
  lastActiveTitle: string;
  bucket: ActivityBucket;
  lastActiveMs: number | null;
}

export interface TenantRollup {
  id: number;
  name: string;
  groupExists: boolean;
  users: number;
  activeWeek: number;
  activeMonth: number;
  mostRecentMs: number | null;
  mostRecentText: string;
}

/** Cumulative headline figures. Each window includes the ones before it. */
export interface ActivityTotals {
  users: number;
  activeToday: number;
  activeWeek: number;
  activeMonth: number;
  never: number;
}

// ── Feature usage: what the metered features are costing, per tenant per month ───────────────

// Keep in sync with dentallink .../tenant/api/dto/AdminFeatureUsageDTO.FeatureUsageRowDTO
export interface FeatureUsageRowDTO {
  tenantId: number;
  tenantName: string;
  feature: FeatureFlag;
  unit: QuotaUnit;
  enabled: boolean;
  /** Spent so far, in booking units: seconds of audio, assistant requests. */
  quantity: number;
  /** How many separate operations that was — notes sent, questions asked. */
  events: number;
  /** LLM tokens burned. Zero for a feature that reaches no model; this is the real bill. */
  tokens: number;
  /** The month's allowance, in the same booking units as `quantity`. */
  allowance: number;
  remaining: number;
  /** The tenant's negotiated figure in admin-facing units, or null when it is on the free plan. */
  ownLimit: number | null;
  /** What is in force — its own figure, or the free plan's. */
  effectiveLimit: number;
}

// GET /v1/admin/usage/features. Uncached, unlike the users report: a stale spend figure is exactly
// the wrong thing to show somebody deciding whether to raise a limit.
export interface FeatureUsageResponse {
  period: string;
  generatedAt: string;
  data: FeatureUsageRowDTO[];
  size: number;
}

/**
 * One tenant's row for one feature with its display strings and bar geometry precomputed.
 * Template-called functions re-run on every change-detection pass; the work is done once, when the
 * response lands and we are already iterating.
 */
export interface FeatureUsageVM extends FeatureUsageRowDTO {
  featureLabel: string;
  /** Spend and allowance in the unit an admin reads — minutes, not seconds. */
  usedLabel: string;
  limitLabel: string;
  /** True when nobody chose the limit, so the row can say the tenant is on the free plan. */
  onFreePlan: boolean;
  percentUsed: number;
  /** 'ok' | 'warn' | 'over' — drives the bar colour, never on its own (the number is always shown). */
  severity: 'ok' | 'warn' | 'over';
}
